import { type Static, Type } from "@sinclair/typebox"
import { Value } from "@sinclair/typebox/value"
import { MatchStateSchema } from "../schemas"

// ---------------------------------------------------------------------------
// Local (pass-and-play) pending match
// ---------------------------------------------------------------------------

const LOCAL_KEY = "craft-force.pending-match"

const PendingMatchDataSchema = Type.Object({
  matchState: MatchStateSchema,
  challengeId: Type.String(),
  hotSeat: Type.Optional(Type.Boolean()),
})

export type PendingMatchData = Static<typeof PendingMatchDataSchema>

function getSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null
  return window.sessionStorage
}

function getLocalStorage(): Storage | null {
  if (typeof window === "undefined") return null
  return window.localStorage
}

function storePendingData<T>(key: string, data: T): void {
  try {
    getSessionStorage()?.setItem(key, JSON.stringify(data))
  } catch {
    // ignore storage errors
  }
}

function readPendingData<T>(
  key: string,
  schema: Parameters<typeof Value.Check>[0],
  consume: boolean,
): T | null {
  try {
    const sessionStorage = getSessionStorage()
    const localStorage = getLocalStorage()
    const raw = sessionStorage?.getItem(key) ?? localStorage?.getItem(key)
    if (!raw) return null
    if (consume) {
      sessionStorage?.removeItem(key)
      localStorage?.removeItem(key)
    } else if (!sessionStorage?.getItem(key) && localStorage?.getItem(key)) {
      // Migrate legacy localStorage payloads into sessionStorage without
      // consuming them so online reconnect survives reloads within the tab.
      sessionStorage?.setItem(key, raw)
      localStorage?.removeItem(key)
    }
    const parsed = JSON.parse(raw)
    return Value.Check(schema, parsed) ? (parsed as T) : null
  } catch {
    return null
  }
}

function clearPendingData(key: string): void {
  try {
    getSessionStorage()?.removeItem(key)
    getLocalStorage()?.removeItem(key)
  } catch {
    // ignore storage errors
  }
}

export function storePendingMatch(data: PendingMatchData): void {
  storePendingData(LOCAL_KEY, data)
}

export function consumePendingMatch(): PendingMatchData | null {
  return readPendingData<PendingMatchData>(
    LOCAL_KEY,
    PendingMatchDataSchema,
    true,
  )
}

// ---------------------------------------------------------------------------
// Online (server-authoritative) pending match
// ---------------------------------------------------------------------------

const ONLINE_KEY = "craft-force.pending-online-match"

const PendingOnlineMatchDataSchema = Type.Object({
  matchId: Type.String({
    minLength: 1,
  }),
  seatId: Type.Union([Type.Literal("seat_a"), Type.Literal("seat_b")]),
  challengeId: Type.String(),
})

export type PendingOnlineMatchData = Static<typeof PendingOnlineMatchDataSchema>

export function storePendingOnlineMatch(data: PendingOnlineMatchData): void {
  storePendingData(ONLINE_KEY, data)
}

export function getPendingOnlineMatch(): PendingOnlineMatchData | null {
  return readPendingData<PendingOnlineMatchData>(
    ONLINE_KEY,
    PendingOnlineMatchDataSchema,
    false,
  )
}

export function clearPendingOnlineMatch(): void {
  clearPendingData(ONLINE_KEY)
}
